import { useRef } from "react";
import {
    Bug,
    FileOutput,
    FileUp,
    Image as ImageIcon,
    MoveDiagonal,
    Palette,
    Settings,
    Share2,
} from "lucide-react";
import { CHR_MOTIFS, useNesGlitch, type ChrSource } from "./NesGlitchContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { HoverInfoIcon } from "../reusable/HoverInfoIcon";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import {
    NES_MASTER_PALETTE_HEX,
    isReservedBlack,
    nesIndexToHex,
} from "../../utils/nesPalette";
import {
    IMAGE_COLOR_CONSISTENCY_OPTIONS,
    IMAGE_FRAME_FIT_OPTIONS,
    IMAGE_IMPORT_ZOOM_MAX,
    IMAGE_IMPORT_ZOOM_MIN,
    IMAGE_PALETTE_STRATEGIES,
    isValidImageColorConsistency,
    isValidImageFrameFit,
    isValidImagePaletteStrategy,
} from "../../utils/nesChrFromImage";
import type { OutputMode } from "../../utils/pixelSvgRender";
import type { GlitchParams } from "../../utils/nesGlitch";

const OUTPUT_MODES: { id: OutputMode; label: string; description: string }[] = [
    {
        id: "raw",
        label: "Raw rects",
        description: "One <rect> per pixel - largest output (~61k rects).",
    },
    {
        id: "greedy-rects",
        label: "Greedy rects",
        description: "Adjacent same-color pixels merged into rectangles.",
    },
    {
        id: "paths",
        label: "Per-color path",
        description: "One <path> per unique color. Smallest file.",
    },
];

const SOURCE_OPTIONS: { id: ChrSource; label: string }[] = [
    { id: "synthetic", label: "Procedural" },
    { id: "image", label: "Image" },
];

export function NesGlitchControls() {
    const {
        chrSource,
        setChrSource,
        seed,
        setSeed,
        motif,
        setMotif,
        hasImportedImage,
        importedImageName,
        importImageFromBlob,
        clearImportedImage,
        importStatus,
        imagePaletteStrategy,
        setImagePaletteStrategy,
        imageFrameFit,
        setImageFrameFit,
        imageImportZoom,
        setImageImportZoom,
        imageColorConsistency,
        setImageColorConsistency,
        bgPatternTable,
        setBgPatternTable,
        sprPatternTable,
        setSprPatternTable,
        scrollX,
        setScrollX,
        scrollY,
        setScrollY,
        mirroring,
        setMirroring,
        glitchedState,
        paletteOverride,
        setPaletteEntry,
        clearPaletteOverride,
        glitchParams,
        setGlitchParams,
        randomizeGlitchSeed,
        outputMode,
        setOutputMode,
        outputWidth,
        setOutputWidth,
        showOutputPane,
        setShowOutputPane,
        downloadSvg,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
    } = useNesGlitch();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        await importImageFromBlob(f, f.name);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const updateGlitch = <K extends keyof GlitchParams>(
        key: K,
        next: Partial<GlitchParams[K]>,
    ) => {
        setGlitchParams({
            ...glitchParams,
            [key]: {
                ...(glitchParams[key] as object),
                ...next,
            } as GlitchParams[K],
        });
    };

    return (
        <div className="panel-controls nes-glitch-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>NES Glitch</h2>
                    <ToolDescriptionPopover dialogLabel="NES Glitch tool description">
                        <p>
                            Builds a static-frame NES PPU model - pattern
                            tables, nametable, attribute table, palette RAM,
                            scroll, and OAM - and renders it as a 256x240 image.
                            Glitches are state mutations: corrupted tile ids,
                            scrambled subpalette assignments, flipped CHR bits,
                            swapped pattern banks, etc.
                        </p>
                        <p>
                            Source the tile bank from a seeded procedural motif,
                            or import a PNG that gets quantized to the NES
                            master palette and sliced into 8x8 tiles.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={downloadSvg}
                    >
                        Download SVG
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="nes-glitch"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Source" icon={ImageIcon}>
                    <div className="field-row">
                        <label className="field-label">Tile source</label>
                    </div>
                    <div className="nes-glitch-segmented">
                        {SOURCE_OPTIONS.map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                className={`nes-glitch-segmented-btn ${chrSource === opt.id ? "nes-glitch-segmented-btn--active" : ""}`}
                                onClick={() => setChrSource(opt.id)}
                                disabled={
                                    opt.id === "image" && !hasImportedImage
                                }
                                aria-pressed={chrSource === opt.id}
                                title={
                                    opt.id === "image" && !hasImportedImage
                                        ? "Import an image first"
                                        : opt.label
                                }
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    <label className="field-label">Image</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="tool-file-btn"
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const f = e.dataTransfer.files?.[0];
                                if (f?.type.startsWith("image/")) {
                                    void importImageFromBlob(f, f.name);
                                }
                            }}
                        >
                            <FileUp
                                className="tool-file-btn__icon"
                                size={18}
                                aria-hidden
                            />
                            <span>
                                {hasImportedImage
                                    ? "Replace image..."
                                    : "Choose image..."}
                            </span>
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="upload-input"
                            onChange={onImageChange}
                        />
                    </div>
                    {importedImageName && (
                        <p className="upload-info">
                            Image: <strong>{importedImageName}</strong>
                        </p>
                    )}
                    {importStatus && (
                        <p className="control-panel-hint">{importStatus}</p>
                    )}
                    {hasImportedImage && (
                        <div className="field-row">
                            <button
                                type="button"
                                className="tool-action-btn"
                                onClick={clearImportedImage}
                            >
                                Clear image
                            </button>
                        </div>
                    )}

                    {chrSource === "synthetic" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Seed: {seed}
                                    <input
                                        type="number"
                                        value={seed}
                                        onChange={(e) =>
                                            setSeed(Number(e.target.value) | 0)
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() =>
                                        setSeed(
                                            Math.floor(
                                                Math.random() * 0xffffffff,
                                            ),
                                        )
                                    }
                                >
                                    Randomize seed
                                </button>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Motif
                                    <select
                                        value={motif}
                                        onChange={(e) =>
                                            setMotif(
                                                e.target.value as typeof motif,
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        {CHR_MOTIFS.map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {m.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </>
                    )}

                    <div className="field-row" style={{ marginTop: "0.5rem" }}>
                        <label className="field-label">
                            <span className="nes-glitch-label-with-info">
                                <span>Frame to 256x240</span>
                                <HoverInfoIcon
                                    text="How the file is cropped or scaled before NES palette quantization. Nearest-neighbor scaling. Zoom applies to cover and stretch modes; contain ignores zoom."
                                    ariaLabel="Pre-scale and crop info"
                                />
                            </span>
                            <select
                                value={imageFrameFit}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (isValidImageFrameFit(v)) {
                                        setImageFrameFit(v);
                                    }
                                }}
                                className="anchor-select"
                            >
                                {IMAGE_FRAME_FIT_OPTIONS.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        {row.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <p className="control-panel-hint">
                            {
                                IMAGE_FRAME_FIT_OPTIONS.find(
                                    (r) => r.id === imageFrameFit,
                                )?.description
                            }
                        </p>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Import zoom: {imageImportZoom.toFixed(2)}x
                            <input
                                type="range"
                                min={IMAGE_IMPORT_ZOOM_MIN}
                                max={IMAGE_IMPORT_ZOOM_MAX}
                                step={0.05}
                                value={imageImportZoom}
                                disabled={imageFrameFit === "contain"}
                                onChange={(e) =>
                                    setImageImportZoom(Number(e.target.value))
                                }
                            />
                        </label>
                        {imageFrameFit === "contain" && (
                            <p className="control-panel-hint">
                                Zoom has no effect in Contain mode.
                            </p>
                        )}
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            <span className="nes-glitch-label-with-info">
                                <span>Color consistency</span>
                                <HoverInfoIcon
                                    text="Optional pass after palette strategy: smooth which subpalette each 16x16 block uses (fewer noisy edges), or pin two accent colors image-wide so all four subpalettes share them."
                                    ariaLabel="Color consistency info"
                                />
                            </span>
                            <select
                                value={imageColorConsistency}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (isValidImageColorConsistency(v)) {
                                        setImageColorConsistency(v);
                                    }
                                }}
                                className="anchor-select"
                            >
                                {IMAGE_COLOR_CONSISTENCY_OPTIONS.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        {row.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <p className="control-panel-hint">
                            {
                                IMAGE_COLOR_CONSISTENCY_OPTIONS.find(
                                    (r) => r.id === imageColorConsistency,
                                )?.description
                            }
                        </p>
                    </div>

                    {hasImportedImage && (
                        <div className="field-row">
                            <label className="field-label">
                                <span className="nes-glitch-label-with-info">
                                    <span>Palette from image</span>
                                    <HoverInfoIcon
                                        text="How the four background subpalettes are inferred from the quantized image. Changing this rebuilds CHR and the nametable from the same file."
                                        ariaLabel="Palette from image info"
                                    />
                                </span>
                                <select
                                    value={imagePaletteStrategy}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (isValidImagePaletteStrategy(v)) {
                                            setImagePaletteStrategy(v);
                                        }
                                    }}
                                    className="anchor-select"
                                >
                                    {IMAGE_PALETTE_STRATEGIES.map((row) => (
                                        <option key={row.id} value={row.id}>
                                            {row.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <p className="control-panel-hint">
                                {
                                    IMAGE_PALETTE_STRATEGIES.find(
                                        (r) => r.id === imagePaletteStrategy,
                                    )?.description
                                }
                            </p>
                        </div>
                    )}

                    <div className="field-row" style={{ marginTop: "0.75rem" }}>
                        <label className="field-label">
                            Background pattern table
                            <select
                                value={bgPatternTable}
                                onChange={(e) =>
                                    setBgPatternTable(
                                        Number(e.target.value) === 1 ? 1 : 0,
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value={0}>Bank 0</option>
                                <option value={1}>Bank 1</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Sprite pattern table
                            <select
                                value={sprPatternTable}
                                onChange={(e) =>
                                    setSprPatternTable(
                                        Number(e.target.value) === 1 ? 1 : 0,
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value={0}>Bank 0</option>
                                <option value={1}>Bank 1</option>
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Palettes" icon={Palette}>
                    <p className="control-panel-hint">
                        32 palette RAM entries pick from the 64-color NES master
                        palette. Index 0 is the universal background; bg
                        subpalettes 0..3 use entries 1..15; sprite subpalettes
                        use entries 17..31.
                    </p>
                    <PaletteEditor
                        palette={glitchedState.palette}
                        onSet={setPaletteEntry}
                    />
                    <div className="field-row" style={{ marginTop: "0.5rem" }}>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={clearPaletteOverride}
                            disabled={paletteOverride === null}
                        >
                            Reset palette to source
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Scroll & mirroring"
                    icon={MoveDiagonal}
                >
                    <div className="field-row">
                        <label className="field-label">
                            Scroll X: {scrollX}
                            <input
                                type="range"
                                min={0}
                                max={255}
                                step={1}
                                value={scrollX}
                                onChange={(e) =>
                                    setScrollX(Number(e.target.value))
                                }
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Scroll Y: {scrollY}
                            <input
                                type="range"
                                min={0}
                                max={239}
                                step={1}
                                value={scrollY}
                                onChange={(e) =>
                                    setScrollY(Number(e.target.value))
                                }
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Mirroring
                            <select
                                value={mirroring}
                                onChange={(e) =>
                                    setMirroring(
                                        e.target.value === "vertical"
                                            ? "vertical"
                                            : "horizontal",
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value="horizontal">Horizontal</option>
                                <option value="vertical">Vertical</option>
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Glitches" icon={Bug}>
                    <div className="field-row">
                        <label className="field-label">
                            Glitch seed: {glitchParams.seed >>> 0}
                            <input
                                type="number"
                                value={glitchParams.seed >>> 0}
                                onChange={(e) =>
                                    setGlitchParams({
                                        ...glitchParams,
                                        seed: Number(e.target.value) | 0,
                                    })
                                }
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={randomizeGlitchSeed}
                        >
                            Randomize glitch seed
                        </button>
                    </div>

                    <GlitchToggleSlider
                        label="Nametable shift"
                        info="Add a constant to every tile id (mod 256). Big values look like a wholesale CHR-bank slip."
                        enabled={glitchParams.nametableShift.enabled}
                        onEnable={(v) =>
                            updateGlitch("nametableShift", { enabled: v })
                        }
                        value={glitchParams.nametableShift.delta}
                        min={-128}
                        max={128}
                        step={1}
                        onValueChange={(v) =>
                            updateGlitch("nametableShift", { delta: v })
                        }
                        valueLabel={(v) => `${v}`}
                    />

                    <GlitchToggleSlider
                        label="Nametable noise"
                        info="Replace random tile ids with random values."
                        enabled={glitchParams.nametableNoise.enabled}
                        onEnable={(v) =>
                            updateGlitch("nametableNoise", { enabled: v })
                        }
                        value={glitchParams.nametableNoise.probability}
                        min={0}
                        max={1}
                        step={0.01}
                        onValueChange={(v) =>
                            updateGlitch("nametableNoise", { probability: v })
                        }
                        valueLabel={(v) => `${Math.round(v * 100)}%`}
                    />

                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={glitchParams.nametableScroll.enabled}
                            onChange={(v) =>
                                updateGlitch("nametableScroll", { enabled: v })
                            }
                            label={
                                <span className="nes-glitch-label-with-info">
                                    <span>Nametable physical scroll</span>
                                    <HoverInfoIcon
                                        text="Slide the nametable contents (independent of PPU scroll)."
                                        ariaLabel="Nametable physical scroll info"
                                    />
                                </span>
                            }
                            inline
                        />
                    </div>
                    {glitchParams.nametableScroll.enabled && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Rows: {glitchParams.nametableScroll.rows}
                                    <input
                                        type="range"
                                        min={-30}
                                        max={30}
                                        step={1}
                                        value={
                                            glitchParams.nametableScroll.rows
                                        }
                                        onChange={(e) =>
                                            updateGlitch("nametableScroll", {
                                                rows: Number(e.target.value),
                                            })
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Cols: {glitchParams.nametableScroll.cols}
                                    <input
                                        type="range"
                                        min={-32}
                                        max={32}
                                        step={1}
                                        value={
                                            glitchParams.nametableScroll.cols
                                        }
                                        onChange={(e) =>
                                            updateGlitch("nametableScroll", {
                                                cols: Number(e.target.value),
                                            })
                                        }
                                    />
                                </label>
                            </div>
                        </>
                    )}

                    <GlitchToggleSlider
                        label="Attribute randomize"
                        info="Flip subpalette assignments per 16x16 quadrant."
                        enabled={glitchParams.attributeRandomize.enabled}
                        onEnable={(v) =>
                            updateGlitch("attributeRandomize", { enabled: v })
                        }
                        value={glitchParams.attributeRandomize.probability}
                        min={0}
                        max={1}
                        step={0.01}
                        onValueChange={(v) =>
                            updateGlitch("attributeRandomize", {
                                probability: v,
                            })
                        }
                        valueLabel={(v) => `${Math.round(v * 100)}%`}
                    />

                    <GlitchToggleSlider
                        label="Palette hue shift"
                        info="Rotate every palette RAM entry by N master indices."
                        enabled={glitchParams.paletteHueShift.enabled}
                        onEnable={(v) =>
                            updateGlitch("paletteHueShift", { enabled: v })
                        }
                        value={glitchParams.paletteHueShift.delta}
                        min={-32}
                        max={32}
                        step={1}
                        onValueChange={(v) =>
                            updateGlitch("paletteHueShift", { delta: v })
                        }
                        valueLabel={(v) => `${v}`}
                    />

                    <GlitchToggleSlider
                        label="Palette swap"
                        info="Replace palette entries with random master indices."
                        enabled={glitchParams.paletteSwap.enabled}
                        onEnable={(v) =>
                            updateGlitch("paletteSwap", { enabled: v })
                        }
                        value={glitchParams.paletteSwap.probability}
                        min={0}
                        max={1}
                        step={0.01}
                        onValueChange={(v) =>
                            updateGlitch("paletteSwap", { probability: v })
                        }
                        valueLabel={(v) => `${Math.round(v * 100)}%`}
                    />

                    <GlitchToggleSlider
                        label="CHR bit flip"
                        info="Flip random bits inside the pattern tables (tile graphics get corrupted)."
                        enabled={glitchParams.chrBitFlip.enabled}
                        onEnable={(v) =>
                            updateGlitch("chrBitFlip", { enabled: v })
                        }
                        value={glitchParams.chrBitFlip.probability}
                        min={0}
                        max={0.05}
                        step={0.0005}
                        onValueChange={(v) =>
                            updateGlitch("chrBitFlip", { probability: v })
                        }
                        valueLabel={(v) => `${(v * 100).toFixed(2)}%`}
                    />

                    <GlitchToggleSlider
                        label="OAM noise (sprites)"
                        info="Inject random sprites at random positions/tiles."
                        enabled={glitchParams.oamNoise.enabled}
                        onEnable={(v) =>
                            updateGlitch("oamNoise", { enabled: v })
                        }
                        value={glitchParams.oamNoise.count}
                        min={0}
                        max={64}
                        step={1}
                        onValueChange={(v) =>
                            updateGlitch("oamNoise", { count: v })
                        }
                        valueLabel={(v) => `${v}`}
                    />

                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={glitchParams.bankSwap.enabled}
                            onChange={(v) =>
                                updateGlitch("bankSwap", { enabled: v })
                            }
                            label={
                                <span className="nes-glitch-label-with-info">
                                    <span>Bank swap</span>
                                    <HoverInfoIcon
                                        text="Swap which pattern table the bg / sprites read from."
                                        ariaLabel="Bank swap info"
                                    />
                                </span>
                            }
                            inline
                        />
                    </div>
                    {glitchParams.bankSwap.enabled && (
                        <div className="field-row">
                            <CheckboxField
                                checked={glitchParams.bankSwap.bg}
                                onChange={(v) =>
                                    updateGlitch("bankSwap", { bg: v })
                                }
                                label="...background"
                                inline
                            />
                            <CheckboxField
                                checked={glitchParams.bankSwap.spr}
                                onChange={(v) =>
                                    updateGlitch("bankSwap", { spr: v })
                                }
                                label="...sprites"
                                inline
                            />
                        </div>
                    )}

                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={glitchParams.mirroringSwap.enabled}
                            onChange={(v) =>
                                updateGlitch("mirroringSwap", { enabled: v })
                            }
                            label={
                                <span className="nes-glitch-label-with-info">
                                    <span>Toggle mirroring</span>
                                    <HoverInfoIcon
                                        text="Flip nametable mirroring (horizontal ↔ vertical)."
                                        ariaLabel="Toggle mirroring info"
                                    />
                                </span>
                            }
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Output" icon={FileOutput}>
                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={showOutputPane}
                            onChange={setShowOutputPane}
                            label="Show output preview"
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Output width (px)
                            <input
                                type="number"
                                min={64}
                                max={2048}
                                step={16}
                                value={outputWidth}
                                onChange={(e) =>
                                    setOutputWidth(Number(e.target.value))
                                }
                            />
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        Exports at {outputWidth} x{" "}
                        {Math.round((outputWidth * 240) / 256)} px while
                        preserving the 256 x 240 pixel grid as the SVG viewBox.
                    </p>
                    <div className="field-row">
                        <label className="field-label">Output mode</label>
                    </div>
                    <div className="nes-glitch-output-modes">
                        {OUTPUT_MODES.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className={`nes-glitch-mode-btn ${outputMode === m.id ? "nes-glitch-mode-btn--active" : ""}`}
                                onClick={() => setOutputMode(m.id)}
                                aria-pressed={outputMode === m.id}
                                title={m.description}
                            >
                                <span className="nes-glitch-mode-btn-label">
                                    {m.label}
                                </span>
                                <span className="nes-glitch-mode-btn-desc">
                                    {m.description}
                                </span>
                            </button>
                        ))}
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        getSvg={() => ({
                            name: "nes-glitch-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["nes-glitch"]}
                    />
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="State summary"
                    icon={Settings}
                    defaultOpen={false}
                >
                    <p className="control-panel-hint">
                        Active bg bank:{" "}
                        <strong>{glitchedState.bgPatternTable}</strong> - sprite
                        bank: <strong>{glitchedState.sprPatternTable}</strong> -
                        mirroring: <strong>{glitchedState.mirroring}</strong>
                    </p>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}

interface PaletteEditorProps {
    palette: Uint8Array;
    onSet: (idx: number, masterIndex: number) => void;
}

function PaletteEditor({ palette, onSet }: PaletteEditorProps) {
    const groups: {
        title: string;
        entries: { idx: number; isMirror: boolean }[];
    }[] = [
        {
            title: "Universal background",
            entries: [{ idx: 0, isMirror: false }],
        },
        {
            title: "Bg subpalettes 0..3",
            entries: [
                { idx: 1, isMirror: false },
                { idx: 2, isMirror: false },
                { idx: 3, isMirror: false },
                { idx: 5, isMirror: false },
                { idx: 6, isMirror: false },
                { idx: 7, isMirror: false },
                { idx: 9, isMirror: false },
                { idx: 10, isMirror: false },
                { idx: 11, isMirror: false },
                { idx: 13, isMirror: false },
                { idx: 14, isMirror: false },
                { idx: 15, isMirror: false },
            ],
        },
        {
            title: "Sprite subpalettes 0..3",
            entries: [
                { idx: 17, isMirror: false },
                { idx: 18, isMirror: false },
                { idx: 19, isMirror: false },
                { idx: 21, isMirror: false },
                { idx: 22, isMirror: false },
                { idx: 23, isMirror: false },
                { idx: 25, isMirror: false },
                { idx: 26, isMirror: false },
                { idx: 27, isMirror: false },
                { idx: 29, isMirror: false },
                { idx: 30, isMirror: false },
                { idx: 31, isMirror: false },
            ],
        },
    ];

    return (
        <div className="nes-glitch-palette-editor">
            {groups.map((g) => (
                <div key={g.title} className="nes-glitch-palette-group">
                    <p className="field-label">{g.title}</p>
                    <div className="nes-glitch-palette-row">
                        {g.entries.map((e) => (
                            <PaletteSlot
                                key={e.idx}
                                idx={e.idx}
                                masterIndex={palette[e.idx] & 0x3f}
                                onChange={(mi) => onSet(e.idx, mi)}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

interface PaletteSlotProps {
    idx: number;
    masterIndex: number;
    onChange: (masterIndex: number) => void;
}

function PaletteSlot({ idx, masterIndex, onChange }: PaletteSlotProps) {
    return (
        <details className="nes-glitch-palette-slot">
            <summary
                className="nes-glitch-palette-swatch"
                style={{ background: nesIndexToHex(masterIndex) }}
                title={`Slot ${idx} -> master 0x${masterIndex.toString(16).padStart(2, "0")} (${nesIndexToHex(masterIndex)})`}
            >
                <span className="nes-glitch-palette-swatch-label">
                    {idx.toString(16).padStart(2, "0")}
                </span>
            </summary>
            <div className="nes-glitch-master-grid">
                {NES_MASTER_PALETTE_HEX.map((hex, i) => {
                    if (isReservedBlack(i)) {
                        return (
                            <div
                                key={i}
                                className="nes-glitch-master-cell nes-glitch-master-cell--disabled"
                                title={`Reserved (0x${i.toString(16).padStart(2, "0")})`}
                            />
                        );
                    }
                    return (
                        <button
                            key={i}
                            type="button"
                            className={`nes-glitch-master-cell ${i === masterIndex ? "nes-glitch-master-cell--active" : ""}`}
                            style={{ background: hex }}
                            title={`0x${i.toString(16).padStart(2, "0")} ${hex}`}
                            onClick={() => onChange(i)}
                        />
                    );
                })}
            </div>
        </details>
    );
}

interface GlitchToggleSliderProps {
    label: string;
    /** Tooltip text shown via HoverInfoIcon next to the label. */
    info?: string;
    enabled: boolean;
    onEnable: (v: boolean) => void;
    value: number;
    min: number;
    max: number;
    step: number;
    onValueChange: (v: number) => void;
    valueLabel: (v: number) => string;
}

function GlitchToggleSlider({
    label,
    info,
    enabled,
    onEnable,
    value,
    min,
    max,
    step,
    onValueChange,
    valueLabel,
}: GlitchToggleSliderProps) {
    return (
        <div className="nes-glitch-glitch-row">
            <div className="field-row field-row--switch">
                <CheckboxField
                    checked={enabled}
                    onChange={onEnable}
                    label={
                        <span className="nes-glitch-label-with-info">
                            <span>{label}</span>
                            {info && (
                                <HoverInfoIcon
                                    text={info}
                                    ariaLabel={`${label} info`}
                                />
                            )}
                        </span>
                    }
                    inline
                />
            </div>
            {enabled && (
                <div className="field-row">
                    <label className="field-label">
                        {label}: {valueLabel(value)}
                        <input
                            type="range"
                            min={min}
                            max={max}
                            step={step}
                            value={value}
                            onChange={(e) =>
                                onValueChange(Number(e.target.value))
                            }
                        />
                    </label>
                </div>
            )}
        </div>
    );
}
